// react
import { useMemo } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// components
import { Button } from '@/components/atoms/Button/Button';
import { Card } from '@/components/atoms/Card/Card';
import { Title } from '@/components/atoms/Title/Title';

import { CardContainer } from '@/components/templates/CardContainer/CardContainer';

// theme
import { theme } from '@/theme';

// assets
// @transform-path ../../../../../../src/assets/keni/call.svg
import readingBook from '@/assets/keni/call.svg';

/**
 * Help renders the help page.
 */
export const Help = ({ className, careCircleId }: HelpProps) => {
  // memos
  const config = useMemo(
    (): Config[] => [
      {
        title: 'Question about care?',
        body: 'Our friendly care experts are on hand to answer care questions. You can either speak to them on the phone, or via the live chat in the bottom right hand corner of your app.',
        color: 'green',
        to: `/book-call`,
        buttonLabel: 'Book a call',
        buttonVariant: 'primary',
      },
      {
        title: 'Read FAQs',
        body: 'Got a question about the KareHero app? Check out our FAQs to see if others are asking the same question.',
        color: 'blue',
        to: `/service/faq`,
        buttonLabel: 'See more',
        buttonVariant: 'secondary',
      },
    ],
    [careCircleId],
  );

  return (
    <Wrapper className={className}>
      <Title isNoMargin>Help</Title>
      <ImageWrapper>
        <img alt='header' src={readingBook} />
      </ImageWrapper>

      <CardContainer maxColumns={1} isPreventHeightMatch>
        {config.map((configItem) => (
          <Card key={configItem.title} color={configItem.color}>
            <Content>
              <CardTitle>{configItem.title}</CardTitle>
              <CardBody>{configItem.body}</CardBody>
              <StyledButton
                ariaLabel={configItem.buttonLabel}
                to={configItem.to}
                variant={configItem.buttonVariant}
              >
                {configItem.buttonLabel}
              </StyledButton>
            </Content>
          </Card>
        ))}
      </CardContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  padding-bottom: ${(props) => props.theme.spacing[48]};
`;

const CardTitle = styled.div`
  width: 100%;
  font: ${(props) => props.theme.typography.heading['3xl']};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const CardBody = styled.div`
  width: 100%;
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.theme.spacing[12]};
  width: 100%;
`;

const ImageWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  height: 192px;
  max-height: 192px;
  overflow: visible;

  ${(props) =>
    props.theme.breakpoint.md(css`
      width: auto;
      object-fit: contain;
      min-height: 256px;
      height: 256px;
      max-height: 256px;
    `)}

  & > img {
    border-radius: 8px;
    width: 300px;
    max-width: 300px;
    height: 100%;
    object-fit: contain;
    user-select: none;
    user-drag: none;
    -webkit-user-drag: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }
`;

const StyledButton = styled(Button)`
  width: 200px;
  margin-top: ${(props) => props.theme.spacing[24]};
`;

interface Config {
  title: string;
  body: string;
  color: keyof typeof theme.color.cards;
  to: string;
  buttonLabel: string;
  buttonVariant: 'primary' | 'secondary';
}

interface HelpProps {
  className?: string;
  careCircleId?: string;
}

Help.displayName = 'Help';

export default Help;
